import { Controller } from '@hotwired/stimulus'
/** Specialized controller for the referral wizard's referral step.
 The school avoidance has a few special behaviors that need to be handled here along with PSY.
 High level, when user selects the school avoidance concern, it will automatically
 trigger enrollment into the program. The enrollment requires BOTH IT and PG services.
 High level, when a user selects direct to psychiatry, it will require an outside therapist attestation.
 It will also disable all other services. **/
export default class extends Controller {
  static targets = [
    'yesInProgramRadio',
    'noInProgramRadio',
    'directToPsyService',
    'savProgramRequiredServices',
    'schoolAvoidanceInfoSection',
    'schoolAvoidanceConcern',
    'attestationContainer',
    'attestationCheckbox',
  ]

  connect() {
    this.updatePsyEnrollment()
    this.updateReferralConcern()
    this.updateSavEnrollment()
  }

  findLabel(checkBoxEl) {
    return checkBoxEl.closest('label') || document.querySelector(`label[for="${checkBoxEl.id}"]`)
  }

  disableServiceOptions(checkBoxEl) {
    checkBoxEl.checked = false
    checkBoxEl.disabled = true
    checkBoxEl.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')

    const checkBoxLabel = this.findLabel(checkBoxEl)
    if (checkBoxLabel) {
      checkBoxLabel.classList.add('cursor-not-allowed', 'pointer-events-none', 'text-charcoal-50')
    }
  }

  requireServiceOptions(checkBoxEl) {
    checkBoxEl.checked = true
    // This is needed for an edge case when toggling from Direct to Psy to SAV causing the services to not submit on first click
    checkBoxEl.disabled = false
    // Avoiding the disabled attribute so we can still submit the services
    checkBoxEl.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')

    // Update label too to avoid toggling value through a label click
    const checkBoxLabel = this.findLabel(checkBoxEl)

    if (checkBoxLabel) {
      checkBoxLabel.classList.add('cursor-not-allowed', 'pointer-events-none')
    }
  }

  resetServiceOptions(checkBoxEl) {
    checkBoxEl.checked = false
    checkBoxEl.disabled = false
    checkBoxEl.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')

    const checkBoxLabel = this.findLabel(checkBoxEl)
    if (checkBoxLabel)
      checkBoxLabel.classList.remove(
        'cursor-not-allowed',
        'pointer-events-none',
        'text-charcoal-50'
      )
  }

  updatePsyConfirmationRequirement(requiredPsy) {
    if (this.hasAttestationContainerTarget) {
      this.attestationContainerTarget.classList.toggle('hidden', !requiredPsy)
    }

    if (this.hasAttestationCheckboxTarget) {
      this.attestationCheckboxTarget.toggleAttribute('required', requiredPsy)
      if (!requiredPsy) this.attestationCheckboxTarget.checked = false
    }
  }

  updateSavEnrollment() {
    // When toggling the school avoidance enrollment, PSY is deselected or not an option
    this.updatePsyConfirmationRequirement(false)

    if (this.hasYesInProgramRadioTarget && this.yesInProgramRadioTarget.checked) {
      for (const serviceCheckbox of this.directToPsyServiceTargets) {
        this.disableServiceOptions(serviceCheckbox)
      }

      for (const serviceCheckbox of this.savProgramRequiredServicesTargets) {
        this.requireServiceOptions(serviceCheckbox)
      }
      // Only show school avoidance info announcement when enrollment is selected
      this.schoolAvoidanceInfoSectionTarget?.classList.remove('hidden')
    } else if (this.hasNoInProgramRadioTarget && this.noInProgramRadioTarget?.checked) {
      for (const serviceCheckbox of this.directToPsyServiceTargets) {
        this.resetServiceOptions(serviceCheckbox)
      }

      for (const serviceCheckbox of this.savProgramRequiredServicesTargets) {
        this.resetServiceOptions(serviceCheckbox)
      }
      // Only show school avoidance info announcement when enrollment is selected
      this.schoolAvoidanceInfoSectionTarget?.classList.add('hidden')
    }
  }

  updatePsyEnrollment() {
    // Direct to psychiatry does not other services but is superceded by school avoidance enrollment (SAV)
    if (!this.hasDirectToPsyServiceTarget) return

    if (this.directToPsyServiceTarget.checked) {
      for (const serviceCheckbox of this.savProgramRequiredServicesTargets) {
        this.disableServiceOptions(serviceCheckbox)
      }
      this.updatePsyConfirmationRequirement(true)
    } else {
      for (const serviceCheckbox of this.savProgramRequiredServicesTargets) {
        if (serviceCheckbox.disabled) {
          this.resetServiceOptions(serviceCheckbox)
        }
      }
      this.updatePsyConfirmationRequirement(false)
    }
  }

  updateReferralConcern() {
    // Auto enroll client into School Avoidance program
    if (this.hasSchoolAvoidanceConcernTarget && this.schoolAvoidanceConcernTarget.checked) {
      if (this.hasYesInProgramRadioTarget) {
        this.yesInProgramRadioTarget.checked = true
      }
    }
    // Trigger the other updates when enrolled
    this.updateSavEnrollment()
  }
}
